import { PhaseInfo } from './types';

/**
 * Detects the current marketing phase of an ad based on its historical performance.
 * 
 * @param spend The historical daily spend series
 * @param revenue The historical daily revenue series
 * @param dates The corresponding dates
 */
export const detectCurrentPhase = (
    spend: number[],
    revenue: number[]
): PhaseInfo => {
    const n = spend.length;

    // 0. Check for paused status (no spend in last 2 days)
    const lastDays = spend.slice(-2);
    if (lastDays.length >= 1 && lastDays.every(s => s === 0)) {
        return {
            current: 'paused',
            reason: 'Ad has been inactive for more than 24 hours (turned off).',
            confidence: 1.0
        };
    }

    // 1. Data sanity check
    const validSpend = spend.filter((v: number) => v > 0);

    if (n < 2 || validSpend.length < 1) {
        return {
            current: 'learning',
            reason: 'Insufficient historical data for phase classification.',
            confidence: 0.9
        };
    }

    // 2. Compute recent metrics
    const recentWindow = Math.min(7, Math.max(2, n));
    const windowSpend = spend.slice(-recentWindow);
    const avgRecentSpend = windowSpend.reduce((a, b) => a + b, 0) / windowSpend.length;

    // Previous window for trend detection
    const prevWindowStart = n - (recentWindow * 2);
    const prevWindowSpend = spend.slice(Math.max(0, prevWindowStart), n - recentWindow);
    const avgPrevSpend = prevWindowSpend.length > 0 ? (prevWindowSpend.reduce((a, b) => a + b, 0) / prevWindowSpend.length) : 0;

    // 3. Trend analysis
    const spendTrend = avgPrevSpend > 0 ? (avgRecentSpend - avgPrevSpend) / avgPrevSpend : 0;

    // ROAS analysis
    const dailyRoas = spend.map((s, i) => (s > 0) ? revenue[i] / s : null);
    const recentRoas = dailyRoas.slice(-recentWindow).filter((v): v is number => v !== null);
    const avgRecentRoas = recentRoas.length > 0 ? (recentRoas.reduce((a, b) => a + b, 0) / recentRoas.length) : 0;

    const prevRoas = dailyRoas.slice(Math.max(0, prevWindowStart), n - recentWindow).filter((v): v is number => v !== null);
    const avgPrevRoas = prevRoas.length > 0 ? (prevRoas.reduce((a, b) => a + b, 0) / prevRoas.length) : 0;
    const roasTrend = avgPrevRoas > 0 ? (avgRecentRoas - avgPrevRoas) / avgPrevRoas : 0;

    // 4. DETECTION LOGIC

    // SUNSET: Clear drop in ROAS and spend over two windows
    if (roasTrend < -0.2 && spendTrend < -0.1 && avgRecentRoas < avgPrevRoas) {
        return {
            current: 'sunset',
            reason: `Performance declining. ROAS dropped by ${(Math.abs(roasTrend) * 100).toFixed(0)}% concurrently with budget reduction.`,
            confidence: 0.75
        };
    }

    // SCALING: Significant increase in spend with acceptable ROAS
    if (spendTrend > 0.25) {
        return {
            current: 'scaling',
            reason: `Aggressive budget scaling observed ( +${(spendTrend * 100).toFixed(0)}% spend week-over-week).`,
            confidence: 0.85
        };
    }

    // TESTING: Low absolute spend or highly erratic ROAS
    if (avgRecentSpend < 25) {
        return {
            current: 'testing',
            reason: 'Ad is running on a minimal testing budget.',
            confidence: 0.8
        };
    }

    // STABILIZING: Consistent spend and ROAS over recent history
    const spendVar = windowSpend.length > 1 ? (Math.max(...windowSpend) - Math.min(...windowSpend)) / avgRecentSpend : 0;
    if (Math.abs(spendTrend) < 0.1 && spendVar < 0.4 && n > 14) {
        return {
            current: 'stabilizing',
            reason: 'Spend and performance have normalized with low volatility.',
            confidence: 0.9
        };
    }

    // Default to LEARNING or STABILIZING based on volume
    if (n < 3) {
        return {
            current: 'learning',
            reason: 'Platform algorithm is still in initial learning phase.',
            confidence: 0.7
        };
    }

    return {
        current: 'stabilizing',
        reason: 'Operational consistency detected within normal parameters.',
        confidence: 0.5
    };
};
